import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, catchError } from 'rxjs';
import { SummaryDetails } from '../models/summary.model';
import { IEnvironment } from '../../ui/models/environment.model';
import { normalizeHttpErrorToGov2Response } from '../utils/gov2-error.util';

export interface Gov2ApiResponse<T> {
  status: boolean;
  message?: string;
  responseCode?: string;
  responseMessage?: string;
  traceId?: string;
  httpStatus?: string;
  httpStatusCode?: number;
  data?: T;
}

export interface RequestAccessResponse {
  smsNotificationRecipient?: string;
  emailNotificationRecipient?: string;
}

/**
 * Service for fetching Gov 2.0 application summary via Hub proxy.
 * This ensures OTP verification is handled consistently with Hub 3.0 applications.
 */
@Injectable({
  providedIn: 'root',
})
export class Gov2SummaryService {
  private readonly apiGatewayBaseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject('environment') environment: IEnvironment
  ) {
    this.apiGatewayBaseUrl = environment.apiGatewayBaseUrl;
  }

  /**
   * Request access (OTP) for a Gov 2.0 application.
   * Sends OTP to the applicant's phone/email registered in Gov 2.0.
   *
   * @param applicationNumber - The Gov 2.0 application number
   * @param locale - User's locale (e.g., 'en-US')
   */
  requestAccess(
    applicationNumber: string,
    locale = 'en-US'
  ): Observable<Gov2ApiResponse<RequestAccessResponse>> {
    const url = `${this.apiGatewayBaseUrl}/application/v1/public/federated/legacy/request-access`;
    const headers = new HttpHeaders()
      .set('applicationNumber', applicationNumber)
      .set('locale', locale);

    return this.http
      .get<Gov2ApiResponse<RequestAccessResponse>>(url, { headers })
      .pipe(
        catchError(error => {
          console.error('Error requesting access for Gov2 application:', error);
          return of(
            normalizeHttpErrorToGov2Response<RequestAccessResponse>(
              error,
              'Failed to request access'
            )
          );
        })
      );
  }

  /**
   * Fetch application summary via Hub proxy after OTP verification.
   *
   * @param applicationNumber - The application number (for OTP verification)
   * @param summaryId - The summary/draft ID to fetch from Gov 2.0
   * @param otp - The OTP to verify
   */
  getApplicationSummary(
    applicationNumber: string,
    summaryId: string,
    otp: string
  ): Observable<Gov2ApiResponse<SummaryDetails>> {
    const url = `${this.apiGatewayBaseUrl}/application/v1/public/federated/legacy/summary`;
    const headers = new HttpHeaders()
      .set('applicationNumber', applicationNumber)
      .set('summaryId', summaryId);

    return this.http
      .get<Gov2ApiResponse<SummaryDetails>>(url, { headers, params: { otp } })
      .pipe(
        catchError(error => {
          console.error('Error fetching Gov2 summary:', error);
          return of(
            normalizeHttpErrorToGov2Response<SummaryDetails>(
              error,
              'Failed to fetch summary'
            )
          );
        })
      );
  }

  /**
   * Fetch a Gov 2.0 attachment by file ID via Hub proxy.
   *
   * @param fileId - The file/document UUID from the Gov 2.0 summary
   * @returns Observable with the attachment data (fileContent, baseName, extension)
   */
  getAttachment(fileId: string): Observable<Gov2ApiResponse<any>> {
    const url = `${this.apiGatewayBaseUrl}/application/v1/public/federated/gov2-attachment`;
    return this.http
      .get<Gov2ApiResponse<any>>(url, { params: { fileId } })
      .pipe(
        catchError(error => {
          console.error('Error fetching Gov2 attachment:', error);
          return of(
            normalizeHttpErrorToGov2Response<any>(
              error,
              'Failed to fetch attachment'
            )
          );
        })
      );
  }

  /**
   * @deprecated Use getApplicationSummary(applicationNumber, summaryId, otp) instead.
   * Direct Gov 2.0 access without OTP is no longer supported.
   */
  getApplicationSummaryDirect(
    baseUrl: string,
    draftId: string
  ): Observable<Gov2ApiResponse<SummaryDetails>> {
    console.warn(
      'Gov2SummaryService: Direct access is deprecated. Use getApplicationSummary with OTP instead.'
    );
    if (!baseUrl) {
      return of({ status: false, message: 'Gov2 API URL not provided' });
    }

    const url = `${baseUrl}/irembo/rest/public/draft/find-by-summary-id`;
    const headers = new HttpHeaders().set('draftId', draftId);

    return this.http
      .get<Gov2ApiResponse<SummaryDetails>>(url, { headers })
      .pipe(
        catchError(error => {
          console.error('Error fetching Gov2 summary:', error);
          return of({
            status: false,
            message: error?.message || 'Failed to fetch summary',
          });
        })
      );
  }
}
